import { unique } from "remeda";
import type React from "react";

import type { TRangeSelection } from "../useRangeSelectionChange";

export const SelectionInfo = ({ children, label }: { children: React.ReactNode; label?: string }) => {
  return (
    <div
      className="w-full flex gap-2 items-center px-4 text-foreground-muted"
      style={{ marginTop: "var(--spacing-scale-small-400, 12px)" }}
    >
      <span>{label ?? "Selected Cell(s):"}</span>
      <span>{children}</span>
    </div>
  );
};

export const rangeLabel = (selection: TRangeSelection) => {
  const result: string[] = [];

  // For each multi column range
  for (let i = 0; i < selection.length; i++) {
    const range = selection[i];
    const columns = unique(range.map((c) => c.headerName));
    const startRow = range[0].rowId;
    const endRow = range[range.length - 1].rowId;

    // If single cell selection
    if (startRow === endRow) result.push(`${columns[0]}: ${startRow}`);
    // If single column in range
    else if (columns.length === 1) {
      result.push(`${columns[0]}: ${startRow} - ${endRow}`);
    }
    // If range is accross multiple columns
    else {
      result.push(`${columns[0]}: ${startRow} - ${columns[columns.length - 1]}: ${endRow}`);
    }
  }

  return result.join(", ");
};

/** Is a cell included in the selection */
export const cellInside = (selection: TRangeSelection, columnId: string, period: string) => {
  for (let i = 0; i < selection.length; i++) {
    const rng = selection[i];
    // Adhoc alerts store the spread formula in `row_id`, while `period` on the
    // selection is only the from-leg. Match either so range filter/highlight work.
    const column = rng.find((c) => c.columnId === columnId && (c.period === period || c.rowId === period));
    if (column) return true;
  }
  return false;
};

export const selectionColumnIds = (selection: TRangeSelection) => [
  ...selection.reduce(
    (a, b) => b.reduce((c, d) => (d.productId ? c.add(d.entityId ?? d.columnId) : c), a),
    new Set<string>(),
  ),
];

export const columnSelectionInfo = (selection: TRangeSelection) => {
  const k = [
    ...selection.reduce((a, b) => b.reduce((c, d) => (d.productId ? c.add(d.headerName) : c), a), new Set<string>()),
  ];
  if (k.length <= 1) return k.join("");
  return `${k[0]} - ${k[k.length - 1]}`;
};

export const rangeSelectionInfo = (selection: TRangeSelection) =>
  selection
    .map((range) => {
      const first = range[0];
      const last = range[range.length - 1];

      const firstPeriod = first?.rowId;
      const lastPeriod = last?.rowId;

      const packageNames = unique(range.map((cell) => cell.headerName));

      if (firstPeriod === lastPeriod) return `${packageNames.join(", ")}: ${firstPeriod}`;

      if (packageNames.length > 1) {
        return `${packageNames[0]}: ${firstPeriod} - ${packageNames[packageNames.length - 1]}: ${lastPeriod}`;
      }

      return `${packageNames[0]}: ${firstPeriod} - ${lastPeriod}`;
    })
    .join(", ");
